import type { User, UserRole } from '../../types';
import { useApp } from '../../context/AppContext';
import { getRoleConfig, getStatusConfig, getPriorityConfig, formatDate } from '../../utils/helpers';
import Modal from '../common/Modal';
import Avatar from '../common/Avatar';
import Badge from '../common/Badge';

interface UserDetailModalProps {
  user: User;
  onClose: () => void;
}

const roles: UserRole[] = ['admin', 'manager', 'developer', 'designer', 'qa'];

export default function UserDetailModal({ user, onClose }: UserDetailModalProps) {
  const { state, dispatch, getTeamById } = useApp();

  const userTeams = user.teamIds.map((id) => getTeamById(id)).filter(Boolean);
  const userTasks = state.tasks.filter((t) => t.assigneeIds.includes(user.id));

  const handleRoleChange = (newRole: UserRole) => {
    dispatch({ type: 'UPDATE_USER', payload: { ...user, role: newRole } });
  };

  const handleTaskClick = (taskId: string) => {
    dispatch({ type: 'SELECT_TASK', payload: taskId });
    onClose();
  };

  const roleConfig = getRoleConfig(user.role);

  return (
    <Modal isOpen onClose={onClose} title="Perfil do Usuário" size="xl">
      <div className="p-6">
        <div className="flex items-start gap-4 mb-6">
          <Avatar name={user.name} color={user.avatarColor} size="xl" isOnline={user.isOnline} />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-slate-800">{user.name}</h3>
            <p className="text-sm text-slate-500">{user.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={roleConfig.color}>{roleConfig.label}</Badge>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-1">Cargo</label>
          <select
            value={user.role}
            onChange={(e) => handleRoleChange(e.target.value as UserRole)}
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
          >
            {roles.map((role) => {
              const cfg = getRoleConfig(role);
              return (
                <option key={role} value={role}>
                  {cfg.label}
                </option>
              );
            })}
          </select>
        </div>

        <div className="mb-6">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Equipes</h4>
          <div className="flex flex-wrap gap-1.5">
            {userTeams.map((team) => (
              <span
                key={team!.id}
                className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border"
                style={{
                  borderColor: `${team!.color}40`,
                  backgroundColor: `${team!.color}10`,
                  color: team!.color,
                }}
              >
                {team!.name}
              </span>
            ))}
            {userTeams.length === 0 && (
              <span className="text-sm text-slate-400">Nenhuma equipe</span>
            )}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-3">Tarefas Atribuídas</h4>
          {userTasks.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Tarefa
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Status
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Prioridade
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Prazo
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {userTasks.map((task) => {
                    const statusCfg = getStatusConfig(task.status);
                    const priorityCfg = getPriorityConfig(task.priority);

                    return (
                      <tr
                        key={task.id}
                        onClick={() => handleTaskClick(task.id)}
                        className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <td className="py-2.5 px-3">
                          <span className="text-sm font-medium text-slate-700">{task.title}</span>
                        </td>
                        <td className="py-2.5 px-3">
                          <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
                        </td>
                        <td className="py-2.5 px-3">
                          <Badge className={priorityCfg.color} dot={priorityCfg.dot}>
                            {priorityCfg.label}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-3">
                          {task.dueDate ? (
                            <span className="text-sm text-slate-500">{formatDate(task.dueDate)}</span>
                          ) : (
                            <span className="text-sm text-slate-300">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-6">Nenhuma tarefa atribuída</p>
          )}
        </div>
      </div>
    </Modal>
  );
}
